"use client";

import { useRouter, usePathname } from "next/navigation";
import { createContext, useContext, useCallback, useEffect } from "react";

type PageTransitionContextValue = {
  navigateWithTransition: (href: string) => void;
};

const PageTransitionContext = createContext<PageTransitionContextValue | null>(null);

export function usePageTransition() {
  return useContext(PageTransitionContext);
}

export function PageTransitionProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();

  const navigateWithTransition = useCallback(
    (href: string) => {
      if (href === pathname) return;
      router.push(href);
    },
    [pathname, router]
  );

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest("a");
      if (!target || !target.href) return;
      if (target.target === "_blank" || target.rel === "external") return;
      try {
        const url = new URL(target.href);
        if (url.origin !== window.location.origin) return;
        if (url.pathname === pathname) return;
        if (url.pathname.startsWith("/")) {
          e.preventDefault();
          navigateWithTransition(url.pathname + url.search + url.hash);
        }
      } catch {
        // Invalid URL, ignore
      }
    };

    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, [pathname, navigateWithTransition]);

  return (
    <PageTransitionContext.Provider value={{ navigateWithTransition }}>
      {children}
    </PageTransitionContext.Provider>
  );
}
